import {
  Component,
  EventEmitter,
  Input,
  Output,
  OnChanges,
  SimpleChanges,
  ChangeDetectionStrategy,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { MatSelectModule } from '@angular/material/select';
import {
  VehicleService,
  CreateVehiclePayload,
  UpdateVehiclePayload,
} from '../../services/vehicle.service';
import { VehicleData } from '../../shared/types';
import { NotificationService } from '../../services/notification.service'; // NotificationService;
import { sanitizeErrorMessage } from '../../shared/utils/error.util';
import { MANUAL_ENTRY_MESSAGE, runFetchVehicleData } from '../../shared/utils/vehicle-fetch.util';
import { vendorPlateSyncWarning } from '../../shared/utils/vendor-plate-sync-warning.util';
import {
  activeMembershipPlateWarning,
  type VehicleActiveMembership,
} from '../../shared/utils/active-membership-plate-warning.util';
import { US_STATES, State } from '../../shared/constants/us-states.constant';
import { getManufacturingYearError } from '../../shared/utils/vehicle-year.util';

export { MANUAL_ENTRY_MESSAGE };

/**
 * AddVehicleCardComponent - A reusable card component for adding or editing a vehicle. It allows users to enter a license plate and state, fetch vehicle details from an API, and create or update a vehicle record while managing UI state and showing success/error messages.
 * @component
 * @selector app-add-vehicle-card
 * @example
 *   <app-add-vehicle-card
 *     [isVisible]="true"
 *     [customerId]="'customer-123'"
 *     [editMode]="false"
 *     [vehicleToEdit]="selectedVehicle"
 *     (cardClosed)="onCardClosed()"
 *     (vehicleAdded)="onVehicleAdded($event)"
 *     (vehicleUpdated)="onVehicleUpdated($event)">
 *   </app-add-vehicle-card>
 * @input {boolean} isVisible - Controls whether the add/edit card is shown.
 * @input {string} customerId - The ID of the customer the new vehicle will be associated with (required for create mode).
 * @input {boolean} editMode - When true, the component operates in edit mode and populates fields from vehicleToEdit.
 * @input {any} vehicleToEdit - Optional vehicle object used to initialize the form in edit mode (expected to include id, name, vin, licensePlate, state, etc.).
 * @output {EventEmitter<void>} cardClosed - Emitted when the card is closed or cancelled (after reset).
 * @output {EventEmitter<any>} vehicleAdded - Emitted with created vehicle data after a successful create operation.
 * @output {EventEmitter<any>} vehicleUpdated - Emitted with updated vehicle data after a successful update operation.
 *
 * Key methods:
 * - onInputChange(): Resets fetched vehicle state and error messages when user changes inputs (state or plate).
 * - onFetchVehicle(): Validates inputs and triggers an async fetch to retrieve vehicle data by license plate and state. Updates isLoading, isVehicleFetched, vehicleData, and errorMessage based on callbacks. A lookup miss opens manual Year/Make/Model entry instead of blocking Save.
 * - onSave(): Orchestrates save flow; chooses createVehicle() or updateVehicle() depending on editMode and validates canSave before proceeding.
 * - createVehicle(): Builds a payload and calls vehicleService.createVehicle to persist a new vehicle; emits vehicleAdded, resets the form, closes the card, and shows messages on success or failure.
 * - updateVehicle(): Builds an update payload and calls vehicleService.updateVehicle for an existing vehicle; emits vehicleUpdated, resets the form, closes the card, and shows messages on success or failure.
 * - onCancel(): Resets the form and emits cardClosed to signal cancellation.
 * - initializeEditMode(): Prepares component state and vehicleData when entering edit mode using vehicleToEdit.
 * - resetForm(): Clears form fields, loading/saving flags, errors, and cached vehicleData.
 * - extractStateFromVehicle(), extractYearFromName(), extractMakeFromName(), extractModelFromName(): Helper utilities to parse and extract vehicle attributes from an incoming vehicle object or its name string.
 *
 * Lifecycle hooks:
 * - ngOnChanges(changes: SimpleChanges): Watches for changes to vehicleToEdit and editMode to initialize edit form or reset the form when mode switches.
 */
@Component({
  selector: 'app-add-vehicle-card',
  standalone: true,
  imports: [CommonModule, FormsModule, MatSelectModule],
  templateUrl: './add-vehicle-card.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrls: ['./add-vehicle-card.component.scss'],
})
export class AddVehicleCardComponent implements OnChanges {
  @Input() isVisible = false;
  @Input() customerId = '';
  @Input() editMode = false;
  @Input() vehicleToEdit: any = null;
  /**
   * `customers.source` for the customer this card belongs to. Drives the vendor plate
   * sync notice (CWS-1384); null/'way' shows nothing.
   */
  @Input() customerSource: string | null = null;

  @Output() cardClosed = new EventEmitter<void>();
  @Output() vehicleAdded = new EventEmitter<any>();
  @Output() vehicleUpdated = new EventEmitter<any>();

  selectedState = '';
  licensePlate = '';

  isVehicleFetched = false;
  isLoading = false;
  isSaving = false;
  errorMessage = '';
  saveErrorMessage = '';

  // Vehicle data from API
  vehicleData: VehicleData | null = null;

  /**
   * Manual entry: AskOtto (or the local vehicles table) had nothing for the plate, so the
   * operator types Year, Make and Model. Save then uses these instead of a lookup result.
   */
  isManualEntry = false;
  readonly manualEntryMessage = MANUAL_ENTRY_MESSAGE;

  /**
   * Vendor sync notice for an imported customer, or null for a Way customer.
   * Informational only — it never blocks the save (CWS-1384).
   */
  get vendorPlateSyncNotice(): string | null {
    return vendorPlateSyncWarning(this.customerSource);
  }

  /** Live memberships on the vehicle being edited; empty when adding a new one. */
  activeMemberships: VehicleActiveMembership[] = [];

  /**
   * Warning that this car carries a live membership which follows the plate (CWS-1384).
   * Informational only — it never blocks the save.
   */
  get activeMembershipNotice(): string | null {
    return activeMembershipPlateWarning(this.activeMemberships);
  }

  /**
   * Best-effort lookup behind the membership warning. A failure leaves the warning off
   * rather than blocking an edit that is legitimate either way.
   */
  private loadActiveMembershipsForWarning(): void {
    this.activeMemberships = [];
    const vehicleId = this.vehicleToEdit?.id;
    if (!this.editMode || !vehicleId) {
      return;
    }
    this.vehicleService.getActiveMemberships(vehicleId).subscribe({
      next: response => {
        this.activeMemberships = response?.data?.memberships ?? [];
      },
      error: () => {
        this.activeMemberships = [];
      },
    });
  }
  manualYear = '';
  manualMake = '';
  manualModel = '';
  manualYearError = '';
  manualMakeError = '';
  manualModelError = '';

  constructor(
    private vehicleService: VehicleService,
    private messageService: NotificationService
  ) {}

  ngOnChanges(changes: SimpleChanges): void {
    // Initialize form when vehicle data changes for edit mode
    if (changes['vehicleToEdit'] || changes['editMode']) {
      if (this.editMode && this.vehicleToEdit) {
        this.initializeEditMode();
      } else if (!this.editMode) {
        this.resetForm();
      }
    }
  }

  /**
   * Initialize the component into edit mode by extracting and populating state, license plate, and vehicle data from this.vehicleToEdit.
   * @example
   * initializeEditMode()
   * // no return value
   * @returns {void} No return value.
   */
  private initializeEditMode(): void {
    if (!this.vehicleToEdit) return;

    // Extract state from license plate or set default
    // Assuming the state might be embedded in vehicle data or we need to extract it
    this.selectedState = this.extractStateFromVehicle(this.vehicleToEdit);
    this.licensePlate = this.vehicleToEdit.licensePlate || '';

    // Mark as fetched and populate vehicle data
    this.isVehicleFetched = true;
    this.vehicleData = {
      status: 'success',
      vin: this.vehicleToEdit.vin || '',
      year: this.extractYearFromName(this.vehicleToEdit.name) || new Date().getFullYear(),
      make: this.extractMakeFromName(this.vehicleToEdit.name) || '',
      model: this.extractModelFromName(this.vehicleToEdit.name) || '',
      state: this.vehicleToEdit.state || '',
      plate: this.licensePlate,
      _utc: '',
      last_updated: '',
      cached: '',
    };

    this.isManualEntry = false;
    this.clearManualFields();

    this.errorMessage = '';
    this.saveErrorMessage = '';

    this.loadActiveMembershipsForWarning();
  }

  private extractStateFromVehicle(vehicle: any): string {
    // Try to extract state from vehicle data
    // This might come from API response or be stored in vehicle object
    return vehicle.state || vehicle.licensePlateState || '';
  }

  private extractYearFromName(name: string): number | null {
    if (!name) return null;
    const yearMatch = name.match(/\b(19|20)\d{2}\b/);
    return yearMatch ? parseInt(yearMatch[0]) : null;
  }

  private extractMakeFromName(name: string): string {
    if (!name) return '';
    const parts = name.split(' ');
    // Skip year if it's the first part
    const firstPart = parts[0];
    if (/^\d{4}$/.test(firstPart)) {
      return parts[1] || '';
    }
    return firstPart || '';
  }

  private extractModelFromName(name: string): string {
    if (!name) return '';
    const parts = name.split(' ');
    // Skip year and make
    if (/^\d{4}$/.test(parts[0])) {
      return parts.slice(2).join(' ') || '';
    }
    return parts.slice(1).join(' ') || '';
  }

  states: State[] = US_STATES;

  get canFetch(): boolean {
    return this.selectedState.trim() !== '' && this.licensePlate.trim() !== '';
  }

  /**
   * Determine whether the vehicle form can be saved (required fields present, customerId required when adding, and not currently saving).
   * @example
   * canSave()
   * true
   * @param {{void}} {{none}} - No parameters.
   * @returns {{boolean}} True if the form can be saved; otherwise false.
   **/
  get canSave(): boolean {
    const hasVehicle = this.isVehicleFetched || this.isManualEntryValid;
    const hasRequiredFields =
      hasVehicle && this.selectedState.trim() !== '' && this.licensePlate.trim() !== '';

    // For edit mode, we don't need customerId validation
    if (this.editMode) {
      return hasRequiredFields && !this.isSaving;
    }

    // For add mode, we need customerId
    return hasRequiredFields && this.customerId.trim() !== '' && !this.isSaving;
  }

  /** True while manual entry is open and every typed field passes validation. */
  get isManualEntryValid(): boolean {
    return (
      this.isManualEntry &&
      this.manualMake.trim() !== '' &&
      this.manualModel.trim() !== '' &&
      !getManufacturingYearError(this.manualYear)
    );
  }

  onInputChange(): void {
    // Reset vehicle fetched status when input changes
    // In edit mode, allow re-fetching if license plate or state changes
    this.isVehicleFetched = false;
    this.vehicleData = null;
    this.errorMessage = '';
    this.saveErrorMessage = '';
    // A new plate needs a new lookup; anything typed for the old one no longer applies.
    this.isManualEntry = false;
    this.clearManualFields();
  }

  onManualYearInput(): void {
    this.manualYear = (this.manualYear ?? '').replace(/\D/g, '').slice(0, 4);
    this.manualYearError = '';
    this.saveErrorMessage = '';
  }

  onManualMakeInput(): void {
    this.manualMakeError = '';
    this.saveErrorMessage = '';
  }

  onManualModelInput(): void {
    this.manualModelError = '';
    this.saveErrorMessage = '';
  }

  /**
   * Open manual entry after a lookup miss with empty Year/Make/Model: nothing was fetched,
   * so the operator starts from a blank vehicle and Save relies on what they type.
   */
  private enableManualEntry(): void {
    this.isVehicleFetched = false;
    this.vehicleData = null;
    this.isManualEntry = true;
    this.clearManualFields();
  }

  private clearManualFields(): void {
    this.manualYear = '';
    this.manualMake = '';
    this.manualModel = '';
    this.clearManualErrors();
  }

  private clearManualErrors(): void {
    this.manualYearError = '';
    this.manualMakeError = '';
    this.manualModelError = '';
  }

  /** Fill per-field messages for manual entry; returns true when Save may proceed. */
  private validateManualEntry(): boolean {
    this.clearManualErrors();
    this.manualYearError = getManufacturingYearError(this.manualYear) ?? '';
    if (!this.manualMake.trim()) {
      this.manualMakeError = 'Make is required';
    }
    if (!this.manualModel.trim()) {
      this.manualModelError = 'Model is required';
    }
    return !this.manualYearError && !this.manualMakeError && !this.manualModelError;
  }

  /** Build the record Save persists from the typed Year/Make/Model. VIN stays empty: nobody looked it up. */
  private buildManualVehicleData(): VehicleData {
    return {
      status: 'manual',
      vin: '',
      year: parseInt(this.manualYear, 10),
      make: this.manualMake.trim(),
      model: this.manualModel.trim(),
      state: this.selectedState,
      plate: this.licensePlate.trim(),
      _utc: '',
      last_updated: '',
      cached: '',
    };
  }

  /**
   * Initiates vehicle data fetch using the component's licensePlate and selectedState; manages loading state and handles success, failure, and error outcomes (displays an error message on error).
   * @example
   * nFetchVehicle()
   * undefined
   * @returns {void} Does not return a value; updates component state (isLoading, vehicleData, errorMessage, isVehicleFetched) and may display an error notification.
   */
  onFetchVehicle(): void {
    if (!this.canFetch || this.isLoading) {
      return;
    }

    this.isLoading = true;
    this.errorMessage = '';
    this.vehicleData = null;
    this.isManualEntry = false;

    runFetchVehicleData(
      this.vehicleService,
      {
        license_plate: this.licensePlate.trim(),
        state: this.selectedState,
        // Read-only: Save writes the vehicle, once, with the customer on it (CWS-1398).
        persist: false,
      },
      {
        onSuccess: data => {
          this.isLoading = false;
          this.vehicleData = data;
          this.isVehicleFetched = true;
          this.isManualEntry = false;
          this.errorMessage = '';
        },
        onFailure: () => {
          // AskOtto miss (or lookup not configured). Do not block the operator: they can
          // still save the plate by typing Year, Make and Model.
          this.isLoading = false;
          this.errorMessage = MANUAL_ENTRY_MESSAGE;
          this.enableManualEntry();
        },
        onError: message => {
          // Transport/server failure. Show what went wrong, but still let the operator
          // type the vehicle in rather than leaving Save dead.
          this.isLoading = false;
          this.errorMessage = sanitizeErrorMessage(message);
          this.enableManualEntry();
          this.messageService.add({
            severity: 'error',
            summary: 'Error',
            detail: this.errorMessage,
          });
        },
      }
    );
  }

  /**
   * Save the current vehicle: if saving is allowed and vehicle data exists, set saving state and call createVehicle or updateVehicle based on editMode.
   * @example
   * nSave()
   * undefined
   * @param {void} none - No arguments.
   * @returns {void} Nothing is returned.
   */
  onSave(): void {
    if (!this.canSave) {
      return;
    }

    if (this.isManualEntry && !this.isVehicleFetched) {
      if (!this.validateManualEntry()) {
        return;
      }
      this.vehicleData = this.buildManualVehicleData();
    }

    if (!this.vehicleData) {
      return;
    }

    this.isSaving = true;
    this.saveErrorMessage = '';

    if (this.editMode) {
      this.updateVehicle();
    } else {
      this.createVehicle();
    }
  }

  /**
   * Creates a vehicle from component state by calling the vehicle service, handling success and error responses, emitting events, resetting the form, and showing user messages.
   * @example
   * createVehicle()
   * undefined
   * @returns {{void}} Performs side effects (saves vehicle, emits vehicleAdded and cardClosed events, resets the form, and displays success/error messages) and does not return a value.
   **/
  /**
   * One failure, read in one place (CWS-1398). Every save error used to print the same
   * sentence twice — once in the toast, once inline in the card — so the inline copy is
   * cleared here and the toast is the single place the operator reads it.
   */
  private showSaveError(message: string, summary = 'Error'): void {
    this.saveErrorMessage = '';
    this.messageService.add({
      severity: 'error',
      summary,
      detail: sanitizeErrorMessage(message),
    });
  }

  private createVehicle(): void {
    const createVehiclePayload: CreateVehiclePayload = {
      license_plate: this.licensePlate.trim(),
      state: this.selectedState,
      vin: this.vehicleData!.vin,
      make: this.vehicleData!.make,
      model: this.vehicleData!.model,
      year: this.vehicleData!.year,
      customer_id: this.customerId,
      is_family_plan: false,
    };

    this.vehicleService.createVehicle(createVehiclePayload).subscribe({
      next: response => {
        this.isSaving = false;
        if (response.already_exists) {
          // This customer already has the plate, so nothing was created. The toast carries
          // the reason on its own; repeating it inside the card made the operator read the
          // same sentence twice (CWS-1398). The card stays open so the plate can be
          // corrected. A plate held by another customer arrives as a 409 below instead.
          this.showSaveError(
            response.message || 'This vehicle has already been added.',
            'Vehicle Already Added'
          );
          return;
        }
        if (response.status) {
          // Emit the created vehicle data
          this.vehicleAdded.emit({
            ...this.vehicleData,
            license_plate: this.licensePlate.trim(),
            state: this.selectedState,
            customer_id: this.customerId,
          });

          // Reset form and close
          this.resetForm();
          this.cardClosed.emit();

          this.messageService.add({
            severity: 'success',
            summary: 'Vehicle Added',
            detail: 'Vehicle has been added successfully.',
          });
        } else {
          this.showSaveError(response.message || 'Failed to save vehicle');
        }
      },
      error: error => {
        this.isSaving = false;
        console.error('Error saving vehicle:', error);
        // The plate belonging to another customer is a refusal with its own name, not a
        // generic failure (CWS-1398).
        this.showSaveError(
          error.error?.message || 'Failed to save vehicle. Please try again.',
          error.error?.data?.code === 'VEHICLE_PLATE_TAKEN' ? 'Plate Already Registered' : 'Error'
        );
      },
    });
  }

  /**
   * Updates an existing vehicle by validating the presence of a vehicle ID, constructing an update payload from form fields, calling the vehicle service to persist changes, handling success and error responses, emitting update and close events, resetting the form, and displaying user feedback messages.
   * @example
   * updateVehicle()
   * undefined
   * @param {void} none - No arguments.
   * @returns {void} Void - Performs side effects (HTTP request, event emission, UI updates) and does not return a value.
   **/
  private updateVehicle(): void {
    if (!this.vehicleToEdit?.id) {
      this.showSaveError('Vehicle ID is missing for update');
      this.isSaving = false;
      return;
    }

    const updateVehiclePayload: UpdateVehiclePayload = {
      license_plate: this.licensePlate.trim(),
      state: this.selectedState,
      vin: this.vehicleData!.vin,
      make: this.vehicleData!.make,
      model: this.vehicleData!.model,
      year: this.vehicleData!.year,
    };

    this.vehicleService.updateVehicle(this.vehicleToEdit.id, updateVehiclePayload).subscribe({
      next: response => {
        this.isSaving = false;
        if (response.status) {
          // Emit the updated vehicle data
          this.vehicleUpdated.emit({
            ...this.vehicleToEdit,
            name: `${this.vehicleData!.year} ${this.vehicleData!.make} ${this.vehicleData!.model}`,
            licensePlate: this.licensePlate.trim(),
            vin: this.vehicleData!.vin,
            state: this.selectedState,
          });

          // Reset form and close
          this.resetForm();
          this.cardClosed.emit();

          this.messageService.add({
            severity: 'success',
            summary: 'Vehicle details updated',
            detail: 'Your changes have been saved..',
          });
        } else {
          this.showSaveError(response.message || 'Failed to update vehicle');
        }
      },
      error: error => {
        this.isSaving = false;
        console.error('Error updating vehicle:', error);
        this.showSaveError(error.error?.message || 'Failed to update vehicle. Please try again.');
      },
    });
  }

  onCancel(): void {
    this.resetForm();
    this.cardClosed.emit();
  }

  private resetForm(): void {
    this.selectedState = '';
    this.licensePlate = '';

    this.isVehicleFetched = false;
    this.isLoading = false;
    this.isSaving = false;
    this.errorMessage = '';
    this.saveErrorMessage = '';
    this.vehicleData = null;

    this.isManualEntry = false;
    this.clearManualFields();
  }
}
